import type {
  AgentRuntimeComputationState,
} from "@/lib/agent-runtime-sync-batch-computation"
import type { ClaudeRawProjection } from "@/lib/claude-raw-runtime"
import type { OpenCodeRawProjection } from "@/lib/opencode-raw-runtime"
import {
  estimateThreadSyncReplayWorkerRequestSize,
} from "@/lib/thread-sync-replay-worker-size-estimation"
import type { ThreadSyncReplayWorkerEvent } from "@/lib/thread-sync-replay-worker-types"

export interface ThreadSyncReplayWorkerStateSummary {
  agentType: AgentRuntimeComputationState["agentType"]
  snapshotTimelineCount: number
  snapshotPendingMessageCount: number
  snapshotPendingInteractionCount: number
  openCodeMessageCount: number
  openCodeChildSessionCount: number
  claudeRawEventCount: number
  chunkEventCount: number
  estimatedCurrentStateBytes: number
  estimatedChunkEventsBytes: number
  estimatedWorkerRequestBytes: number
  estimatedResponseProjectionBytes: number | null
  currentStateSizeEstimateWasTruncated: boolean
  chunkEventsSizeEstimateWasTruncated: boolean
  responseProjectionSizeEstimateWasTruncated: boolean
}

export type ThreadSyncReplayWorkerBypassReason =
  | "snapshot_timeline_too_large"
  | "opencode_message_graph_too_large"
  | "claude_raw_event_history_too_large"
  | "runtime_chunk_budget_too_large"
  | "estimated_current_state_bytes_too_large"
  | "estimated_request_bytes_too_large"
  | "estimated_response_projection_bytes_too_large"

export type ThreadSyncReplayWorkerRequestShape = "start" | "continue"

export type ThreadSyncReplayWorkerEligibility =
  | {
      allowed: true
      summary: ThreadSyncReplayWorkerStateSummary
    }
  | {
      allowed: false
      reason: ThreadSyncReplayWorkerBypassReason
      summary: ThreadSyncReplayWorkerStateSummary
    }

export type ThreadSyncDurableReplicaCoveragePolicy =
  | {
      mode: "keep_full"
      summary: ThreadSyncReplayWorkerStateSummary
    }
  | {
      mode: "compact_to_tail_window"
      reason: "runtime_too_large"
      retainRuntimeEventCount: number
      summary: ThreadSyncReplayWorkerStateSummary
    }

/**
 * Once the visible runtime timeline is well into the low-thousands, sending the
 * full runtime state across a worker boundary for every replay chunk is more
 * likely to amplify structured-clone pressure than to help interactivity. This
 * first-pass cap is intentionally conservative so pathological sessions fail
 * safe; tune it upward only with fresh traces proving browser headroom.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_SNAPSHOT_TIMELINE_ITEMS = 1_200

/**
 * OpenCode projections carry a rich normalized graph plus derived timeline
 * caches. A few hundred messages is enough for the whole-state worker clone to
 * become risky on real large-thread repros, so protect the browser with a
 * conservative message-graph ceiling until instrumentation gives us better
 * byte-based cutoffs.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_OPENCODE_MESSAGES = 400

/**
 * Claude replay cost scales directly with retained raw event history. Allow a
 * moderate low-thousands event window for worker offload, but bypass it once
 * the raw event list gets large enough that cloning the full projection is more
 * dangerous than the saved main-thread compute.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_CLAUDE_EVENTS = 1_600

/**
 * The new traces still show worker response delivery becoming a hotspot before
 * we hit the absolute "too large" cutoffs above. Once replay is processing a
 * fairly full chunk against an already-large runtime, structured-cloning the
 * request and result often costs more than the off-main-thread compute saves.
 * Keep this chunk-aware guard comfortably below the hard stops so those sessions
 * fail safe onto the synchronous path earlier.
 */
export const THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_CHUNK_EVENTS = 64
export const THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_SNAPSHOT_TIMELINE_ITEMS = 800
export const THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_OPENCODE_MESSAGES = 250
export const THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_CLAUDE_EVENTS = 1_000
/**
 * The latest traces show replay-worker response delivery becoming the next
 * bottleneck only after we have already split catchup into many chunk-sized
 * round trips. Once we are headed for a few dozen worker responses, use a much
 * stricter medium-runtime budget so we fail safe to the synchronous path before
 * structured-clone overhead stacks up across the whole catchup sequence.
 */
export const THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_TOTAL_CHUNKS = 24
export const THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_SNAPSHOT_TIMELINE_ITEMS =
  Math.floor(THREAD_SYNC_REPLAY_WORKER_MAX_SNAPSHOT_TIMELINE_ITEMS / 2)
export const THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_OPENCODE_MESSAGES =
  Math.floor(THREAD_SYNC_REPLAY_WORKER_MAX_OPENCODE_MESSAGES / 2)
export const THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_CLAUDE_EVENTS =
  Math.floor(THREAD_SYNC_REPLAY_WORKER_MAX_CLAUDE_EVENTS / 2)

/**
 * The server already caps websocket catchup pages at 256 KiB. Reusing that as
 * the browser-side current-state ceiling keeps worker requests from cloning a
 * runtime snapshot much larger than one network page before any chunk events are
 * added on top.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_CURRENT_STATE_BYTES = 256 * 1024

/**
 * The worker request contains both the cloned runtime state and the current
 * replay chunk, so allow some headroom above the state-only ceiling while still
 * keeping the whole request well below a half-megabyte clone. If real traces
 * show safe headroom, tune this deliberately instead of letting it drift.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_REQUEST_BYTES = 384 * 1024

/**
 * Continued worker-session requests no longer resend the full runtime state, but
 * changed batches still clone the updated raw projection back to the main
 * thread. Reuse the one-page-sized state ceiling so continuation responses can
 * still fail safe if that projection grows too large.
 */
export const THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_RESPONSE_PROJECTION_BYTES =
  THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_CURRENT_STATE_BYTES

/**
 * Persisting full durable coverage is still useful for many large sessions, but
 * once the visible timeline grows to roughly twice the replay-worker safety
 * threshold the snapshot payload itself is likely large enough that IndexedDB
 * serialization becomes part of the problem. At that point fall back to a
 * bounded durable tail window instead of forcing the browser to carry full
 * coverage for an oversized runtime.
 */
export const THREAD_SYNC_DURABLE_REPLICA_MAX_FULL_COVERAGE_TIMELINE_ITEMS =
  THREAD_SYNC_REPLAY_WORKER_MAX_SNAPSHOT_TIMELINE_ITEMS * 2

/**
 * Keep the durable tail window near the largest snapshot timeline we still
 * consider safe to rebuild in one replay/hydrate pass. This bounds retained
 * replica history without degrading large threads all the way to cold replay;
 * if traces show hydrate cost is still too high, tighten this with data rather
 * than shrinking resumable coverage arbitrarily.
 */
export const THREAD_SYNC_DURABLE_REPLICA_TAIL_WINDOW_RETAIN_RUNTIME_EVENT_COUNT =
  THREAD_SYNC_REPLAY_WORKER_MAX_SNAPSHOT_TIMELINE_ITEMS

function countOpenCodeMessages(projection: OpenCodeRawProjection | null): number {
  if (!projection) {
    return 0
  }

  if (Array.isArray(projection.orderedMessageIds)) {
    return projection.orderedMessageIds.length
  }

  return Object.keys(projection.messagesById ?? {}).length
}

function countOpenCodeChildSessions(projection: OpenCodeRawProjection | null): number {
  if (!projection) {
    return 0
  }

  if (Array.isArray(projection.orderedChildSessionIds)) {
    return projection.orderedChildSessionIds.length
  }

  return Object.keys(projection.childSessionStateBySessionId ?? {}).length
}

function countClaudeRawEvents(projection: ClaudeRawProjection | null): number {
  return Array.isArray(projection?.events) ? projection.events.length : 0
}

function buildThreadSyncReplayWorkerCountSummary(args: {
  currentState: AgentRuntimeComputationState
  chunkEventCount: number
}): ThreadSyncReplayWorkerStateSummary {
  return {
    agentType: args.currentState.agentType,
    snapshotTimelineCount: args.currentState.snapshot?.timeline.length ?? 0,
    snapshotPendingMessageCount: args.currentState.snapshot?.pendingMessages.length ?? 0,
    snapshotPendingInteractionCount: args.currentState.snapshot?.pendingInteractions.length ?? 0,
    openCodeMessageCount: countOpenCodeMessages(args.currentState.rawOpenCodeProjection),
    openCodeChildSessionCount: countOpenCodeChildSessions(args.currentState.rawOpenCodeProjection),
    claudeRawEventCount: countClaudeRawEvents(args.currentState.rawClaudeProjection),
    chunkEventCount: args.chunkEventCount,
    estimatedCurrentStateBytes: 0,
    estimatedChunkEventsBytes: 0,
    estimatedWorkerRequestBytes: 0,
    estimatedResponseProjectionBytes: null,
    currentStateSizeEstimateWasTruncated: false,
    chunkEventsSizeEstimateWasTruncated: false,
    responseProjectionSizeEstimateWasTruncated: false,
  }
}

function applyThreadSyncReplayWorkerByteEstimates(args: {
  summary: ThreadSyncReplayWorkerStateSummary
  currentState: AgentRuntimeComputationState
  chunkEvents?: ThreadSyncReplayWorkerEvent[]
  includeCurrentStateBytes: boolean
  continuedProjectionBytes?: number | null
  continuedProjectionBytesWasTruncated?: boolean
}): ThreadSyncReplayWorkerStateSummary {
  const sizeEstimate = estimateThreadSyncReplayWorkerRequestSize({
    currentState: args.includeCurrentStateBytes
      ? args.currentState
      : {
          agentType: null,
          snapshot: null,
          lastAppliedTimestamp: null,
          lastEventId: null,
          rawOpenCodeProjection: null,
          rawClaudeProjection: null,
          rawCursorProjection: null,
        },
    events: args.chunkEvents ?? [],
  })

  return {
    ...args.summary,
    estimatedCurrentStateBytes: sizeEstimate.currentStateBytes,
    estimatedChunkEventsBytes: sizeEstimate.chunkEventsBytes,
    estimatedWorkerRequestBytes: sizeEstimate.requestBytes,
    estimatedResponseProjectionBytes:
      typeof args.continuedProjectionBytes === "number" ? args.continuedProjectionBytes : null,
    currentStateSizeEstimateWasTruncated: sizeEstimate.currentStateWasTruncated,
    chunkEventsSizeEstimateWasTruncated: sizeEstimate.chunkEventsWereTruncated,
    responseProjectionSizeEstimateWasTruncated: args.continuedProjectionBytesWasTruncated ?? false,
  }
}

export function buildThreadSyncReplayWorkerStateSummary(args: {
  currentState: AgentRuntimeComputationState
  chunkEventCount: number
  chunkEvents?: ThreadSyncReplayWorkerEvent[]
  requestShape?: ThreadSyncReplayWorkerRequestShape
  continuedProjectionBytes?: number | null
  continuedProjectionBytesWasTruncated?: boolean
}): ThreadSyncReplayWorkerStateSummary {
  return applyThreadSyncReplayWorkerByteEstimates({
    summary: buildThreadSyncReplayWorkerCountSummary(args),
    currentState: args.currentState,
    chunkEvents: args.chunkEvents,
    includeCurrentStateBytes: args.requestShape !== "continue",
    continuedProjectionBytes: args.continuedProjectionBytes,
    continuedProjectionBytesWasTruncated: args.continuedProjectionBytesWasTruncated,
  })
}

function evaluateThreadSyncReplayWorkerCountBudget(args: {
  summary: ThreadSyncReplayWorkerStateSummary
  currentState: AgentRuntimeComputationState
  totalChunkCount: number | null
}): ThreadSyncReplayWorkerEligibility | null {
  if (args.summary.snapshotTimelineCount > THREAD_SYNC_REPLAY_WORKER_MAX_SNAPSHOT_TIMELINE_ITEMS) {
    return {
      allowed: false,
      reason: "snapshot_timeline_too_large",
      summary: args.summary,
    }
  }

  if (
    args.currentState.agentType === "opencode"
    && args.summary.openCodeMessageCount > THREAD_SYNC_REPLAY_WORKER_MAX_OPENCODE_MESSAGES
  ) {
    return {
      allowed: false,
      reason: "opencode_message_graph_too_large",
      summary: args.summary,
    }
  }

  if (
    args.currentState.agentType === "claude_code"
    && args.summary.claudeRawEventCount > THREAD_SYNC_REPLAY_WORKER_MAX_CLAUDE_EVENTS
  ) {
    return {
      allowed: false,
      reason: "claude_raw_event_history_too_large",
      summary: args.summary,
    }
  }

  const isLongChunkedCatchup =
    args.totalChunkCount !== null
    && args.totalChunkCount >= THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_TOTAL_CHUNKS
  const chunkedRuntimeSnapshotBudget = isLongChunkedCatchup
    ? THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_SNAPSHOT_TIMELINE_ITEMS
    : THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_SNAPSHOT_TIMELINE_ITEMS
  const chunkedRuntimeOpenCodeBudget = isLongChunkedCatchup
    ? THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_OPENCODE_MESSAGES
    : THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_OPENCODE_MESSAGES
  const chunkedRuntimeClaudeBudget = isLongChunkedCatchup
    ? THREAD_SYNC_REPLAY_WORKER_LONG_CATCHUP_MAX_CLAUDE_EVENTS
    : THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_CLAUDE_EVENTS
  const exceedsChunkedRuntimeBudget =
    args.summary.chunkEventCount >= THREAD_SYNC_REPLAY_WORKER_CHUNKED_RUNTIME_MAX_CHUNK_EVENTS
    && (
      args.summary.snapshotTimelineCount > chunkedRuntimeSnapshotBudget
      || (
        args.currentState.agentType === "opencode"
        && args.summary.openCodeMessageCount > chunkedRuntimeOpenCodeBudget
      )
      || (
        args.currentState.agentType === "claude_code"
        && args.summary.claudeRawEventCount > chunkedRuntimeClaudeBudget
      )
    )
  if (exceedsChunkedRuntimeBudget) {
    return {
      allowed: false,
      reason: "runtime_chunk_budget_too_large",
      summary: args.summary,
    }
  }

  return null
}

export function evaluateThreadSyncReplayWorkerEligibility(args: {
  summary?: ThreadSyncReplayWorkerStateSummary
  currentState: AgentRuntimeComputationState
  chunkEventCount: number
  chunkEvents?: ThreadSyncReplayWorkerEvent[]
  requestShape?: ThreadSyncReplayWorkerRequestShape
  continuedProjectionBytes?: number | null
  continuedProjectionBytesWasTruncated?: boolean
  totalChunkCount?: number | null
}): ThreadSyncReplayWorkerEligibility {
  const totalChunkCount =
    typeof args.totalChunkCount === "number"
      && Number.isFinite(args.totalChunkCount)
      && args.totalChunkCount > 0
      ? Math.floor(args.totalChunkCount)
      : null
  const countSummary = args.summary ?? buildThreadSyncReplayWorkerCountSummary(args)
  const countBudgetResult = evaluateThreadSyncReplayWorkerCountBudget({
    summary: countSummary,
    currentState: args.currentState,
    totalChunkCount,
  })
  if (countBudgetResult) {
    return countBudgetResult
  }

  if (args.continuedProjectionBytesWasTruncated) {
    return {
      allowed: false,
      reason: "estimated_response_projection_bytes_too_large",
      summary: {
        ...countSummary,
        estimatedResponseProjectionBytes: typeof args.continuedProjectionBytes === "number"
          ? args.continuedProjectionBytes
          : null,
        responseProjectionSizeEstimateWasTruncated: true,
      },
    }
  }

  if (
    typeof args.continuedProjectionBytes === "number"
    && args.continuedProjectionBytes > THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_RESPONSE_PROJECTION_BYTES
  ) {
    return {
      allowed: false,
      reason: "estimated_response_projection_bytes_too_large",
      summary: {
        ...countSummary,
        estimatedResponseProjectionBytes: args.continuedProjectionBytes,
      },
    }
  }

  const summary = args.summary ?? applyThreadSyncReplayWorkerByteEstimates({
    summary: countSummary,
    currentState: args.currentState,
    chunkEvents: args.chunkEvents,
    includeCurrentStateBytes: args.requestShape !== "continue",
    continuedProjectionBytes: args.continuedProjectionBytes,
    continuedProjectionBytesWasTruncated: args.continuedProjectionBytesWasTruncated,
  })

  if (summary.currentStateSizeEstimateWasTruncated) {
    return {
      allowed: false,
      reason: "estimated_current_state_bytes_too_large",
      summary,
    }
  }

  if (summary.estimatedCurrentStateBytes > THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_CURRENT_STATE_BYTES) {
    return {
      allowed: false,
      reason: "estimated_current_state_bytes_too_large",
      summary,
    }
  }

  if (summary.chunkEventsSizeEstimateWasTruncated) {
    return {
      allowed: false,
      reason: "estimated_request_bytes_too_large",
      summary,
    }
  }

  if (summary.estimatedWorkerRequestBytes > THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_REQUEST_BYTES) {
    return {
      allowed: false,
      reason: "estimated_request_bytes_too_large",
      summary,
    }
  }

  if (summary.responseProjectionSizeEstimateWasTruncated) {
    return {
      allowed: false,
      reason: "estimated_response_projection_bytes_too_large",
      summary,
    }
  }

  if (
    summary.estimatedResponseProjectionBytes !== null
    && summary.estimatedResponseProjectionBytes > THREAD_SYNC_REPLAY_WORKER_MAX_ESTIMATED_RESPONSE_PROJECTION_BYTES
  ) {
    return {
      allowed: false,
      reason: "estimated_response_projection_bytes_too_large",
      summary,
    }
  }

  return {
    allowed: true,
    summary,
  }
}

function shouldCompactThreadSyncDurableReplicaToTailWindow(args: {
  currentState: AgentRuntimeComputationState
}): {
  shouldCompact: boolean
  summary: ThreadSyncReplayWorkerStateSummary
} {
  const countSummary = buildThreadSyncReplayWorkerCountSummary({
    currentState: args.currentState,
    chunkEventCount: 0,
  })

  if (
    countSummary.snapshotTimelineCount >
    THREAD_SYNC_DURABLE_REPLICA_MAX_FULL_COVERAGE_TIMELINE_ITEMS
  ) {
    return {
      shouldCompact: true,
      summary: countSummary,
    }
  }

  const countBudgetResult = evaluateThreadSyncReplayWorkerCountBudget({
    summary: countSummary,
    currentState: args.currentState,
    totalChunkCount: null,
  })
  if (countBudgetResult) {
    return {
      shouldCompact: true,
      summary: countSummary,
    }
  }

  const summary = buildThreadSyncReplayWorkerStateSummary({
    currentState: args.currentState,
    chunkEventCount: 0,
  })

  const replayWorkerEligibility = evaluateThreadSyncReplayWorkerEligibility({
    summary,
    currentState: args.currentState,
    chunkEventCount: 0,
  })

  return {
    shouldCompact: !replayWorkerEligibility.allowed,
    summary,
  }
}

export function evaluateThreadSyncDurableReplicaCoveragePolicy(args: {
  currentState: AgentRuntimeComputationState
}): ThreadSyncDurableReplicaCoveragePolicy {
  const retentionPolicy = shouldCompactThreadSyncDurableReplicaToTailWindow({
    currentState: args.currentState,
  })

  if (!retentionPolicy.shouldCompact) {
    return {
      mode: "keep_full",
      summary: retentionPolicy.summary,
    }
  }

  return {
    mode: "compact_to_tail_window",
    reason: "runtime_too_large",
    retainRuntimeEventCount: THREAD_SYNC_DURABLE_REPLICA_TAIL_WINDOW_RETAIN_RUNTIME_EVENT_COUNT,
    summary: retentionPolicy.summary,
  }
}
